import { useState } from "react";
import styles from "./InlinePanel.module.css";

interface TableSplitPanelProps {
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onApply: (headerRows: number, splitRow: number) => void;
}

/**
 * Inline slide-down panel for Split Table (ported from the VSTO "Table Split"
 * dialog): how many header rows to repeat on the new slide, and which row to
 * split at. Renders in the page flow below the Table Tools section, not a
 * centred modal.
 */
export function TableSplitPanel({ busy, onCancel, onApply }: TableSplitPanelProps): React.ReactElement {
  const [headerRows, setHeaderRows] = useState(1);
  const [splitRow, setSplitRow] = useState(2);

  const canApply = headerRows >= 1 && splitRow >= 1;

  return (
    <fieldset className={styles.panel} aria-label="Table Split options">
      <div className={styles.header}>
        <span className={styles.title}>Table Split</span>
        <button type="button" className={styles.close} aria-label="Close" onClick={onCancel}>
          &times;
        </button>
      </div>

      <div className={styles.row}>
        <label htmlFor="ts-headers">No. of header rows</label>
        <input
          id="ts-headers"
          type="number"
          min={1}
          value={headerRows}
          disabled={busy}
          aria-label="Number of header rows"
          onChange={(e) => {
            const n = Number.parseInt(e.target.value, 10);
            if (!Number.isNaN(n)) setHeaderRows(Math.max(1, n));
          }}
        />
      </div>

      <div className={styles.row}>
        <label htmlFor="ts-split">Row number to split</label>
        <input
          id="ts-split"
          type="number"
          min={1}
          value={splitRow}
          disabled={busy}
          aria-label="Row number to split"
          onChange={(e) => {
            const n = Number.parseInt(e.target.value, 10);
            if (!Number.isNaN(n)) setSplitRow(Math.max(1, n));
          }}
        />
      </div>

      <div className={styles.footer}>
        <button type="button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button
          type="button"
          className={styles.primary}
          onClick={() => onApply(headerRows, splitRow)}
          disabled={busy || !canApply}
        >
          {busy ? "Working…" : "OK"}
        </button>
      </div>
    </fieldset>
  );
}
